import React, { useEffect, useState } from 'react';
import { Typography, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom-v5-compat';
import { useSelector } from 'react-redux';

import {
    useCheckAuthState,
    usePaymentConnections,
    useUrlParams
} from '@/hooks';
import { ConnectionSuccessful } from '@/resources/images';
import { updateBusinessSettings } from '@/modules/dataWrangler/dataWrangler';
import { isMerchantCapabilityActive } from '@/util';
import useStyles from './styles';
import GenericDialog from '../GenericDialog';

const ThirdPartyConnectionModal = () => {
    const theme = useTheme();
    const classes = useStyles(theme);

    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    const { loadingUser } = useCheckAuthState();
    const { getUrlParams, setUrlParams } = useUrlParams();

    const urlParams = getUrlParams();
    const accountSuccess = urlParams?.success;
    const urlProcessor = urlParams.processor;
    const activeBusiness = useSelector((state) => state?.user?.activeBusiness);

    const { connections, preferredPaymentConnections } =
        usePaymentConnections();

    const turnOnSwitches = async () => {
        const processorConnection = connections?.find(
            (c) => c.application === urlProcessor
        );

        const connectionId = processorConnection?.connectionId;

        const { otherConnections } = preferredPaymentConnections;

        const cashappId =
            Boolean(connections.length > 0) &&
            connections.find((c) => c.application === 'cashapp')?.connectionId;

        const updatedPreferredPaymentConnections = {
            ...preferredPaymentConnections,
            // For Stripe connections, we need to check if the payment channel is available as a capability.
            // If the connection is Stripe and the capability is not 'active', do not update that channel.
            creditCard: isMerchantCapabilityActive(
                'Credit Cards',
                processorConnection
            )
                ? connectionId
                : preferredPaymentConnections?.creditCard,
            digitalWallet: isMerchantCapabilityActive(
                'Digital Wallets',
                processorConnection
            )
                ? connectionId
                : preferredPaymentConnections?.digitalWallet,
            bankTransfer: isMerchantCapabilityActive(
                'Bank Transfers',
                processorConnection
            )
                ? connectionId
                : preferredPaymentConnections?.bankTransfer,
            otherConnections:
                cashappId && urlProcessor === 'square'
                    ? otherConnections.filter((c) => c !== cashappId)
                    : otherConnections
        };

        await updateBusinessSettings({
            ...activeBusiness,
            businessSettings: {
                ...activeBusiness.businessSettings,
                preferredPaymentConnections: updatedPreferredPaymentConnections
            }
        });

        setUrlParams({});

        setOpen(false);
    };

    useEffect(() => {
        if (
            accountSuccess === 'true' &&
            pathname === '/settings/payment-methods'
        ) {
            setOpen(true);
        }
    }, [accountSuccess, pathname, activeBusiness?.businessId]);

    return (
        <GenericDialog
            onConfirm={turnOnSwitches}
            customIcon={
                <div className={classes.iconContainer}>
                    <ConnectionSuccessful className={classes.icon} />
                </div>
            }
            confirmColor={theme.palette.primary.main}
            title=""
            message={
                <Typography color="textPrimary">
                    Your account has been connected.
                </Typography>
            }
            confirmText="Ok"
            large
            open={connections.length > 0 && !loadingUser && open}
        />
    );
};

export default ThirdPartyConnectionModal;
